import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ScanLine, X } from 'lucide-react';
import { getBackendUrl } from '../../services/origin';

const API = getBackendUrl();

/* ── COUPON QR SCANNER ──────────────────────────────────────────
   Lets the customer scan a coupon QR (or a campaign QR) without
   leaving the dashboard. Uses the html5-qrcode library lazy-loaded
   so the dashboard's first render isn't slowed by it. */
export function CouponQRScanner({ token, pin, brand, tc, ts, setData }) {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scannerRef = useRef(null);
  const containerId = 'coupon-qr-reader';
  const pc = brand.primary_color;

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch { /* ignore */ }
    setScanning(false);
  };

  useEffect(() => { return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll while the camera modal is visible — prevents the
  // page underneath from scrolling on iOS Safari and avoids weird
  // double-scroll behavior on Android.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // The dashboard QR scanner is intentionally GENERIC:
  //   • any http(s) URL → we navigate to it (works for campaign flyers,
  //     coupon URLs with `?code=`, printed QR with `/coupon/<code>`,
  //     even a third-party site we don't know about);
  //   • any other text → we send it as-is to the redeem-coupon endpoint
  //     (the backend validates: if it's a real coupon the customer gets
  //     it; otherwise the backend's error message is shown once and the
  //     modal closes — no infinite loop on the same frame).
  const handleDetected = useCallback(async (decoded) => {
    if (submitting) return;
    setSubmitting(true);
    await stopScanner();

    const raw = (decoded || '').trim();
    if (!raw) {
      toast.error('QR vuoto', { duration: 3000 });
      setOpen(false);
      setSubmitting(false);
      return;
    }

    // URL → navigate. For same-origin URLs we use a relative path so the
    // SPA picks it up; cross-origin (e.g. flyer printed on production
    // host while the customer is on the preview host, or a generic
    // landing page) opens directly. Failing to parse falls through to
    // the coupon-redeem branch below.
    if (/^https?:\/\//i.test(raw)) {
      try {
        const url = new URL(raw);
        // If the destination is a campaign landing on the same brand,
        // append the dashboard token so the landing page can recognize
        // the customer (already authenticated via PIN in this PWA) and
        // skip the phone+PIN login form. Token-only — no PIN in URL.
        const isCampaign = /^\/c\/[^/]+\/?$/i.test(url.pathname);
        if (isCampaign && token && !url.searchParams.has('dt')) {
          url.searchParams.set('dt', token);
        }
        setOpen(false);
        setSubmitting(false);
        if (url.origin === window.location.origin) {
          window.location.assign(url.pathname + url.search + url.hash);
        } else {
          window.location.assign(url.toString());
        }
        return;
      } catch { /* fallthrough to coupon attempt */ }
    }

    // Anything else → try as a coupon code. Whitespace-stripped, upper.
    const couponCode = raw.replace(/\s+/g, '').toUpperCase();
    try {
      const res = await axios.post(`${API}/api/my/${token}/redeem-coupon`, {
        pin, coupon_code: couponCode,
      });
      toast.success(res.data?.message || 'Coupon riscattato!');
      try {
        const refreshed = await axios.get(`${API}/api/my/${token}/data?pin=${pin}`);
        setData(refreshed.data);
      } catch { /* non-fatal */ }
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'QR non valido';
      toast.error(detail, { duration: 4500 });
      // Always close the modal on error — the camera was still framing
      // the same QR and re-arming would loop on the same toast.
      setOpen(false);
    } finally { setSubmitting(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, token, setData, submitting]);

  const startScanner = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const Html5Qrcode = mod.Html5Qrcode || mod.default?.Html5Qrcode;
      // Make sure any previous instance is gone first.
      try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } } catch {}
      const scanner = new Html5Qrcode(containerId, /* verbose */ false);
      scannerRef.current = scanner;
      // Use a square qrbox sized to ~70% of the smallest viewport edge so
      // the camera frame stays inside the modal on mobile portrait.
      const minEdge = Math.min(window.innerWidth, window.innerHeight);
      const box = Math.max(180, Math.min(360, Math.floor(minEdge * 0.7)));
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: box, height: box }, aspectRatio: 1.0 },
        (decoded) => handleDetected(decoded),
        () => { /* per-frame: ignore */ }
      );
    } catch (e) {
      toast.error('Impossibile accedere alla fotocamera. Controlla i permessi nel browser.');
      setScanning(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(startScanner, 250); }}
        data-testid="open-qr-scanner-btn"
        className="w-full h-12 rounded-2xl border flex items-center justify-center gap-2 font-semibold text-sm transition-all active:scale-[0.98]"
        style={{ backgroundColor: `${pc}15`, borderColor: `${pc}30`, color: pc }}
      >
        <ScanLine size={18} />
        Scansiona QR code
      </button>

      {open && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[60] bg-black flex flex-col"
          onClick={() => { stopScanner(); setOpen(false); }}
          data-testid="qr-scanner-modal"
        >
          <div
            className="flex-1 flex flex-col"
            onClick={e => e.stopPropagation()}
            style={{ backgroundColor: '#000' }}
          >
            {/* Header — semi-transparent over camera */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, transparent 100%)' }}>
              <p className="text-white font-semibold text-sm">Scansiona un QR</p>
              <button
                onClick={() => { stopScanner(); setOpen(false); }}
                data-testid="close-qr-scanner-btn"
                className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-white active:scale-90"
              >
                <X size={18} />
              </button>
            </div>
            {/* Camera viewfinder fills the rest of the screen */}
            <div className="relative flex-1 overflow-hidden flex items-center justify-center">
              <div
                id={containerId}
                className="w-full h-full"
                style={{
                  // html5-qrcode injects a <video> here; force it to cover
                  // the area with object-fit so it doesn't shrink to its
                  // intrinsic resolution.
                }}
              />
              <style>{`
                #${containerId} video { width: 100% !important; height: 100% !important; object-fit: cover !important; }
                #${containerId} { display: flex; align-items: center; justify-content: center; }
              `}</style>
              {!scanning && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-white">
                  Avvio fotocamera…
                </div>
              )}
              {/* Decorative aim square */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-[70vmin] max-w-[360px] h-[70vmin] max-h-[360px] rounded-2xl border-2 border-white/40 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
              </div>
            </div>
            {/* Footer hint */}
            <div className="px-5 py-4 text-center text-xs text-white/80" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.65) 0%, transparent 100%)' }}>
              Inquadra qualsiasi QR (volantino, coupon, link).
              {submitting && <p className="mt-2 font-semibold" style={{ color: pc }}>Lettura in corso…</p>}
            </div>
          </div>
        </div>
        , document.body
      )}
    </>
  );
}
